import React, { useEffect, useMemo, useState } from "react";
import api from "../api/api";
import toast from "react-hot-toast";
import {
  Package, Plus, X, Check, XCircle, Clock, Send, MapPin,
  User as UserIcon, MessageSquare, CheckCircle2, Hourglass, Ban
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

type Req = {
  id: string;
  category: string;
  categoryOther: string | null;
  title: string;
  description: string | null;
  amount: number | null;
  location: string;
  status: string;
  decisionNote: string | null;
  decidedAt: string | null;
  assignmentNote: string | null;
  assignedAt: string | null;
  completionNote: string | null;
  completedAt: string | null;
  createdAt: string;
  employee: { id: string; name: string; email: string; department: string | null };
  approvedBy?: { id: string; name: string } | null;
  assignedTo?: { id: string; name: string; email: string } | null;
};

const CATEGORIES: { key: string; label: string; emoji: string }[] = [
  { key: "ADVANCE",     label: "Salary Advance (سلفة)",         emoji: "💰" },
  { key: "EQUIPMENT",   label: "Equipment / IT",                emoji: "💻" },
  { key: "MAINTENANCE", label: "Maintenance",                   emoji: "🔧" },
  { key: "SUPPLIES",    label: "Office Supplies",               emoji: "📦" },
  { key: "ACCESS",      label: "Access / Permissions",          emoji: "🔑" },
  { key: "HR_DOCUMENT", label: "HR Document",                   emoji: "📋" },
  { key: "COMPLAIN",    label: "Complaint (شكوى)",              emoji: "⚠️" },
  { key: "OTHER",       label: "Other",                         emoji: "⚙️" },
];

const catMeta = (k: string) => CATEGORIES.find(c => c.key === k) || CATEGORIES[6];

const STATUS_META: Record<string, { color: string; bg: string; icon: any; label: string }> = {
  PENDING:   { color: "text-amber-700",  bg: "bg-amber-50",   icon: Hourglass,     label: "Pending" },
  APPROVED:  { color: "text-green-700",  bg: "bg-green-50",   icon: Check,         label: "Approved" },
  REJECTED:  { color: "text-red-700",    bg: "bg-red-50",     icon: XCircle,       label: "Rejected" },
  ASSIGNED:  { color: "text-purple-700", bg: "bg-purple-50",  icon: Send,          label: "Assigned" },
  COMPLETED: { color: "text-blue-700",   bg: "bg-blue-50",    icon: CheckCircle2,  label: "Completed" },
  CANCELLED: { color: "text-gray-600",   bg: "bg-gray-100",   icon: Ban,           label: "Cancelled" },
};

const LOCATION_LABEL: Record<string, string> = { SA: "🇸🇦 Saudi Arabia", EG: "🇪🇬 Egypt" };

export default function ServiceRequests() {
  const { user, isAdmin } = useAuth();
  const isHR = user?.role === "HR";
  const [tab, setTab] = useState<"my" | "pending" | "all" | "assigned">("my");
  const [rows, setRows] = useState<Req[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState<Req | null>(null);

  // Form state
  const [category, setCategory] = useState("ADVANCE");
  const [categoryOther, setCategoryOther] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [location, setLocation] = useState("SA");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users").then(r => setUsers((r.data || []).filter((u: any) => u.isActive !== false && !u.isDeleted)))
      .catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    let endpoint = "/service-requests/my";
    if (tab === "pending") endpoint = "/service-requests/pending";
    else if (tab === "all") endpoint = "/service-requests/all";
    else if (tab === "assigned") endpoint = "/service-requests/assigned-to-me";
    api.get<Req[]>(endpoint)
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [tab]);

  const submit = async () => {
    if (!title.trim()) return toast.error("Title required");
    if (category === "OTHER" && !categoryOther.trim()) return toast.error("Please specify the request type");
    setSaving(true);
    try {
      await api.post("/service-requests", {
        category,
        categoryOther: category === "OTHER" ? categoryOther.trim() : undefined,
        title: title.trim(),
        description: description.trim() || undefined,
        amount: amount.trim() ? Number(amount) : undefined,
        location,
      });
      toast.success("Request submitted");
      setShowForm(false);
      setCategory("ADVANCE"); setCategoryOther(""); setTitle(""); setDescription(""); setAmount(""); setLocation("SA");
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally { setSaving(false); }
  };

  const availableTabs: { key: any; label: string; count?: number }[] = [
    { key: "my", label: "My Requests" },
    ...(isAdmin ? [{ key: "pending" as const, label: "Pending Approval" }] : []),
    ...(isAdmin || isHR ? [{ key: "all" as const, label: "All Requests" }] : []),
    { key: "assigned", label: "Assigned to Me" },
  ];

  return (
    <div className="max-w-7xl mx-auto p-3 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Package className="text-indigo-600" size={26}/>
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Service Requests</h1>
            <p className="text-xs sm:text-sm text-gray-500">Company requests: advances, equipment, supplies, maintenance & more</p>
          </div>
        </div>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm">
          <Plus size={16}/> New Request
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200 overflow-x-auto">
        {availableTabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition ${
              tab === t.key ? "border-indigo-600 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-6 text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">
          {tab === "pending" ? "No pending requests. 🎉" : "No requests to display."}
        </div>
      ) : (
        (() => {
          const activeRows = rows.filter(r => !["COMPLETED","CANCELLED"].includes(r.status));
          const doneRows   = rows.filter(r =>  ["COMPLETED","CANCELLED"].includes(r.status));

          const Card = ({ r, finished }: { r: Req; finished: boolean }) => {
            const m = STATUS_META[r.status] || STATUS_META.PENDING;
            const Icon = m.icon;
            const c = catMeta(r.category);
            return (
              <div key={r.id} onClick={() => setDetail(r)}
                className={`rounded-xl p-4 cursor-pointer transition ${
                  finished
                    ? "bg-gray-50 border border-gray-200 opacity-80 hover:opacity-100 hover:border-gray-300"
                    : "bg-white border border-gray-200 hover:shadow-md hover:border-indigo-300"
                }`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-xl ${finished ? "grayscale" : ""}`}>{c.emoji}</span>
                    <div className="min-w-0">
                      <div className={`font-semibold truncate ${finished ? "text-gray-600 line-through" : "text-gray-900"}`}>{r.title}</div>
                      <div className="text-[11px] text-gray-500">{c.label}{r.categoryOther ? ` — ${r.categoryOther}` : ""}</div>
                    </div>
                  </div>
                  <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${m.bg} ${m.color}`}>
                    <Icon size={11}/> {m.label}
                  </span>
                </div>
                <div className="text-xs text-gray-600 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><UserIcon size={11} className="text-gray-400"/>{r.employee.name}</span>
                  <span className="flex items-center gap-1"><MapPin size={11} className="text-gray-400"/>{LOCATION_LABEL[r.location]}</span>
                  {r.amount != null && <span className="font-semibold text-emerald-700">💵 {r.amount}</span>}
                </div>
                {r.assignedTo && !finished && (
                  <div className="mt-2 text-[11px] text-purple-700 bg-purple-50 rounded-md px-2 py-1 inline-flex items-center gap-1">
                    <Send size={10}/> Assigned to {r.assignedTo.name}
                  </div>
                )}
                {finished && r.completedAt && (
                  <div className="mt-2 text-[11px] text-blue-700 bg-blue-50 rounded-md px-2 py-1 inline-flex items-center gap-1">
                    <CheckCircle2 size={10}/> Done {new Date(r.completedAt).toLocaleDateString()}
                    {r.assignedTo ? ` by ${r.assignedTo.name}` : ""}
                  </div>
                )}
                <div className="text-[11px] text-gray-400 mt-2">{new Date(r.createdAt).toLocaleString()}</div>
              </div>
            );
          };

          return (
            <div className="space-y-6">
              {/* Active */}
              {activeRows.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"/>
                    <h2 className="text-sm font-semibold text-gray-800">Active</h2>
                    <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">{activeRows.length}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {activeRows.map(r => <Card key={r.id} r={r} finished={false}/>)}
                  </div>
                </section>
              )}

              {/* Finished */}
              {doneRows.length > 0 && (
                <section className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 size={14} className="text-blue-600"/>
                    <h2 className="text-sm font-semibold text-gray-700">Finished</h2>
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">{doneRows.length}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {doneRows.map(r => <Card key={r.id} r={r} finished={true}/>)}
                  </div>
                </section>
              )}
            </div>
          );
        })()
      )}

      {showForm && (
        <NewForm
          category={category} setCategory={setCategory}
          categoryOther={categoryOther} setCategoryOther={setCategoryOther}
          title={title} setTitle={setTitle}
          description={description} setDescription={setDescription}
          amount={amount} setAmount={setAmount}
          location={location} setLocation={setLocation}
          saving={saving} onCancel={() => setShowForm(false)} onSubmit={submit}
        />
      )}

      {detail && (
        <DetailModal req={detail} users={users} isAdmin={isAdmin} currentUserId={user?.id}
          onClose={() => setDetail(null)} onChanged={() => { setDetail(null); load(); }} />
      )}
    </div>
  );
}

function NewForm(p: any) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={p.onCancel}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b bg-indigo-50 flex items-center justify-between">
          <div className="text-lg font-semibold text-indigo-800">New Service Request</div>
          <button onClick={p.onCancel} className="text-gray-500 hover:text-gray-800"><X size={20}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select value={p.category} onChange={e => p.setCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
            </select>
          </div>
          {p.category === "OTHER" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Specify type *</label>
              <input value={p.categoryOther} onChange={(e: any) => p.setCategoryOther(e.target.value)}
                placeholder="e.g., Business Card printing"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input value={p.title} onChange={(e: any) => p.setTitle(e.target.value)}
              placeholder="Short summary of your request"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <div className="grid grid-cols-2 gap-2">
              {["SA", "EG"].map(loc => (
                <button key={loc} type="button" onClick={() => p.setLocation(loc)}
                  className={`px-3 py-2 rounded-lg border text-sm ${
                    p.location === loc ? "bg-indigo-50 border-indigo-500 text-indigo-800 font-semibold" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}>
                  {LOCATION_LABEL[loc]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount <span className="text-gray-400 text-xs font-normal">(optional — for financial requests)</span>
            </label>
            <input type="number" min="0" step="0.01" value={p.amount} onChange={(e: any) => p.setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-gray-400 text-xs font-normal">(optional)</span>
            </label>
            <textarea value={p.description} onChange={(e: any) => p.setDescription(e.target.value)} rows={3}
              placeholder="Details, context, urgency..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={p.onCancel} className="px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 text-sm">Cancel</button>
            <button onClick={p.onSubmit} disabled={p.saving}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm disabled:opacity-50">
              {p.saving ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ req, users, isAdmin, currentUserId, onClose, onChanged }: any) {
  const [note, setNote] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [action, setAction] = useState<"" | "approve" | "reject" | "assign" | "complete">("");
  const [saving, setSaving] = useState(false);
  const m = STATUS_META[req.status];
  const c = catMeta(req.category);

  const isAssignee = req.assignedTo?.id === currentUserId;
  const canApprove = isAdmin && req.status === "PENDING";
  const canAssign = isAdmin && ["APPROVED", "ASSIGNED"].includes(req.status);
  const canComplete = (isAssignee || isAdmin) && req.status === "ASSIGNED";
  const canCancel = (req.employee.id === currentUserId || isAdmin) && !["COMPLETED", "CANCELLED"].includes(req.status);

  const submit = async () => {
    setSaving(true);
    try {
      if (action === "approve") await api.post(`/service-requests/${req.id}/approve`, { note: note.trim() || undefined });
      else if (action === "reject") await api.post(`/service-requests/${req.id}/reject`, { note: note.trim() || undefined });
      else if (action === "assign") {
        if (!assigneeId) { toast.error("Choose an assignee"); setSaving(false); return; }
        await api.post(`/service-requests/${req.id}/assign`, { assigneeId, note: note.trim() || undefined });
      }
      else if (action === "complete") await api.post(`/service-requests/${req.id}/complete`, { note: note.trim() || undefined });
      toast.success("Saved");
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally { setSaving(false); }
  };

  const doCancel = async () => {
    if (!window.confirm("Cancel this request?")) return;
    setSaving(true);
    try { await api.post(`/service-requests/${req.id}/cancel`, {}); toast.success("Cancelled"); onChanged(); }
    catch (err: any) { toast.error(err?.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className={`px-5 py-4 border-b flex items-start justify-between gap-3 ${m.bg}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">{c.emoji}</span>
              <div className={`text-lg font-semibold ${m.color} truncate`}>{req.title}</div>
            </div>
            <div className="text-xs text-gray-600">{c.label}{req.categoryOther ? ` — ${req.categoryOther}` : ""}</div>
            <div className="text-xs text-gray-500 mt-1">
              by {req.employee.name} · {LOCATION_LABEL[req.location]} · {new Date(req.createdAt).toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 shrink-0"><X size={20}/></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${m.bg} ${m.color} border border-current`}>
              Status: {m.label}
            </span>
          </div>
          {req.amount != null && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <span className="text-xs text-emerald-600">Amount</span>
              <div className="text-lg font-bold text-emerald-700">💵 {req.amount}</div>
            </div>
          )}
          {req.description && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Description</div>
              <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{req.description}</div>
            </div>
          )}
          {req.decisionNote && (
            <div className={`rounded-lg p-3 border ${req.status === "REJECTED" ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
              <div className="text-xs font-semibold mb-1">Admin note ({new Date(req.decidedAt).toLocaleString()})</div>
              <div className="text-sm">{req.decisionNote}</div>
              {req.approvedBy && <div className="text-[11px] text-gray-500 mt-1">— {req.approvedBy.name}</div>}
            </div>
          )}
          {req.assignedTo && (
            <div className="rounded-lg p-3 border bg-purple-50 border-purple-200">
              <div className="text-xs font-semibold text-purple-700 mb-1 flex items-center gap-1">
                <Send size={12}/> Assigned to {req.assignedTo.name} ({new Date(req.assignedAt).toLocaleString()})
              </div>
              {req.assignmentNote && <div className="text-sm text-purple-800">{req.assignmentNote}</div>}
            </div>
          )}
          {req.completionNote && (
            <div className="rounded-lg p-3 border bg-blue-50 border-blue-200">
              <div className="text-xs font-semibold text-blue-700 mb-1">Completion note ({new Date(req.completedAt).toLocaleString()})</div>
              <div className="text-sm text-blue-800">{req.completionNote}</div>
            </div>
          )}

          {(canApprove || canAssign || canComplete) && (
            <div className="pt-3 border-t border-gray-100 space-y-2">
              <div className="flex flex-wrap gap-2">
                {canApprove && (
                  <>
                    <button onClick={() => setAction("approve")}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium border ${action === "approve" ? "bg-green-600 text-white border-green-600" : "bg-white border-green-300 text-green-700 hover:bg-green-50"}`}>
                      ✅ Approve
                    </button>
                    <button onClick={() => setAction("reject")}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium border ${action === "reject" ? "bg-red-600 text-white border-red-600" : "bg-white border-red-300 text-red-700 hover:bg-red-50"}`}>
                      ❌ Reject
                    </button>
                  </>
                )}
                {canAssign && (
                  <button onClick={() => setAction("assign")}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border ${action === "assign" ? "bg-purple-600 text-white border-purple-600" : "bg-white border-purple-300 text-purple-700 hover:bg-purple-50"}`}>
                    🎯 {req.assignedTo ? "Re-assign" : "Assign"}
                  </button>
                )}
                {canComplete && (
                  <button onClick={() => setAction("complete")}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border ${action === "complete" ? "bg-blue-600 text-white border-blue-600" : "bg-white border-blue-300 text-blue-700 hover:bg-blue-50"}`}>
                    ✔️ Mark Completed
                  </button>
                )}
              </div>
              {action && (
                <div className="space-y-2 pt-2">
                  {action === "assign" && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Assignee</label>
                      <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">Select employee...</option>
                        {users.map((u: any) => <option key={u.id} value={u.id}>{u.name} — {u.department || "—"}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Note (optional)</label>
                    <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                      placeholder="Add a note..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setAction("")} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-md">Cancel</button>
                    <button onClick={submit} disabled={saving}
                      className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                      {saving ? "Saving..." : "Confirm"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {canCancel && !action && (
            <div className="pt-3 border-t border-gray-100">
              <button onClick={doCancel} disabled={saving}
                className="text-xs text-red-600 hover:underline">Cancel request</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
